import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader, Center } from '@mantine/core';
import { MineMinerRoute } from '../components/routing/MineMinerRoute';
import { MineClientRoute } from '../components/routing/MineClientRoute';
import { OrganizationRoute } from '../components/routing/OrganizationRoute';
import { HomeRedirect } from '../components/routing/HomeRedirect';
import { ProtectedRoute } from '../components/routing/ProtectedRoute';
import { MineMinerAdminRoute } from '../components/routing/MineMinerAdminRoute';

// Layouts
import { MineMinerLayout } from '../layouts/MineMinerLayout';
import { MineClientLayout } from '../layouts/MineClientLayout';

// Route constants
import {
  PUBLIC_ROUTES,
  UTILITY_ROUTES,
  SIGNED_IN_ROUTES,
  ADMIN_ONLY_ROUTES,
  MINEMINER_ROUTES,
  CLIENT_ROUTES,
  ORGANIZATION_ROUTES,
} from './routes';

// Eagerly-loaded surfaces
import { LoginPage } from '../pages/Login/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPassword/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPassword/ResetPasswordPage';
import { AcceptInvitePage } from '../pages/AcceptInvite/AcceptInvitePage';
import { AccessDenied } from '../pages/AccessDenied/AccessDenied';
import { NotFound } from '../pages/NotFound/NotFound';

// Page loading fallback component
const PageLoader: React.FC = () => (
  <Center h="100vh">
    <Loader />
  </Center>
);

// ============================================================
// LAZY-LOADED MineMiner pages (one per route — code-split)
// ============================================================
const MineMinerMinesListPage = lazy(() =>
  import('../pages/mineminer/MinesListPage').then((m) => ({ default: m.MinesListPage })),
);
const MineMinerMineDetailPage = lazy(() =>
  import('../pages/mineminer/MineDetailPage').then((m) => ({ default: m.MineDetailPage })),
);
// Sprint-233 — single-inspection drill-down off the mine detail inspections section.
const MineMinerMineInspectionDetailPage = lazy(() =>
  import('../pages/mineminer/MineInspectionDetailPage').then((m) => ({
    default: m.MineInspectionDetailPage,
  })),
);
const MineMinerViolationsExplorerPage = lazy(() =>
  import('../pages/mineminer/ViolationsExplorerPage').then((m) => ({
    default: m.ViolationsExplorerPage,
  })),
);
const MineMinerAdminPage = lazy(() =>
  import('../pages/mineminer/MineMinerAdminPage').then((m) => ({
    default: m.MineMinerAdminPage,
  })),
);
const MineMinerNewMineLeadsListPage = lazy(() =>
  import('../pages/mineminer/NewMineLeadsListPage').then((m) => ({
    default: m.NewMineLeadsListPage,
  })),
);
const MineMinerRecentCitationLeadsListPage = lazy(() =>
  import('../pages/mineminer/RecentCitationLeadsListPage').then((m) => ({
    default: m.RecentCitationLeadsListPage,
  })),
);
const MineMinerImminentDangerLeadsListPage = lazy(() =>
  import('../pages/mineminer/ImminentDangerLeadsListPage').then((m) => ({
    default: m.ImminentDangerLeadsListPage,
  })),
);
const MineMinerLeadDetailPage = lazy(() =>
  import('../pages/mineminer/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })),
);
const MineMinerOperatorsListPage = lazy(() =>
  import('../pages/mineminer/OperatorsListPage').then((m) => ({
    default: m.OperatorsListPage,
  })),
);
const MineMinerOperatorDetailPage = lazy(() =>
  import('../pages/mineminer/OperatorDetailPage').then((m) => ({
    default: m.OperatorDetailPage,
  })),
);
const MineMinerControllersListPage = lazy(() =>
  import('../pages/mineminer/ControllersListPage').then((m) => ({
    default: m.ControllersListPage,
  })),
);
const MineMinerControllerDetailPage = lazy(() =>
  import('../pages/mineminer/ControllerDetailPage').then((m) => ({
    default: m.ControllerDetailPage,
  })),
);
const MineMinerContractorsListPage = lazy(() =>
  import('../pages/mineminer/ContractorsListPage').then((m) => ({
    default: m.ContractorsListPage,
  })),
);
const MineMinerContractorDetailPage = lazy(() =>
  import('../pages/mineminer/ContractorDetailPage').then((m) => ({
    default: m.ContractorDetailPage,
  })),
);
const MineMinerContactsListPage = lazy(() =>
  import('../pages/mineminer/ContactsListPage').then((m) => ({
    default: m.ContactsListPage,
  })),
);
const MineMinerContactDetailPage = lazy(() =>
  import('../pages/mineminer/ContactDetailPage').then((m) => ({
    default: m.ContactDetailPage,
  })),
);
// sprint-125 (epic-019 Sprint D) — Companies "Coming Soon" stub (ADR-001).
// Full Account list/detail UI = TECH_DEBT #129.
const MineMinerCompaniesComingSoonPage = lazy(() =>
  import('../pages/mineminer/CompaniesComingSoonPage').then((m) => ({
    default: m.CompaniesComingSoonPage,
  })),
);
// sprint-239 (epic-039 Canary increment A) — the organization area's landing shell.
const OrganizationIndexPage = lazy(() =>
  import('../pages/organization/OrganizationHomePage').then((m) => ({
    default: m.OrganizationIndexPage,
  })),
);
const OrganizationHomePage = lazy(() =>
  import('../pages/organization/OrganizationHomePage').then((m) => ({
    default: m.OrganizationHomePage,
  })),
);
const MineMinerMobileCapturePage = lazy(() =>
  import('../pages/mineminer/MobileCapturePage').then((m) => ({
    default: m.MobileCapturePage,
  })),
);
const MineMinerEventDetailPage = lazy(() =>
  import('../pages/mineminer/EventDetailPage').then((m) => ({
    default: m.EventDetailPage,
  })),
);
const MineMinerEventsListPage = lazy(() =>
  import('../pages/mineminer/EventsListPage').then((m) => ({
    default: m.EventsListPage,
  })),
);
const MineMinerEventExhibitorImportPage = lazy(() =>
  import('../pages/mineminer/events/EventExhibitorImportPage').then((m) => ({
    default: m.EventExhibitorImportPage,
  })),
);
const MineMinerListsPage = lazy(() =>
  import('../pages/mineminer/MineMinerListsPage').then((m) => ({
    default: m.MineMinerListsPage,
  })),
);
const MineMinerListDetailPage = lazy(() =>
  import('../pages/mineminer/MineMinerListDetailPage').then((m) => ({
    default: m.MineMinerListDetailPage,
  })),
);
const MineMinerWorkQueuePage = lazy(() =>
  import('../pages/mineminer/WorkQueuePage').then((m) => ({ default: m.WorkQueuePage })),
);
const MineMinerCampaignsListPage = lazy(() =>
  import('../pages/mineminer/CampaignsListPage').then((m) => ({
    default: m.CampaignsListPage,
  })),
);
const MineMinerCampaignDetailPage = lazy(() =>
  import('../pages/mineminer/CampaignDetailPage').then((m) => ({
    default: m.CampaignDetailPage,
  })),
);
// sprint-110 (epic-015 Sprint B) — user-authored email templates surface.
// Detail page handles both edit + create via the sentinel `:emailTemplateId
// === 'new'`. S1 ships the list page + a stub detail page; S2 fills in the
// TipTap-backed editor.
const MineMinerEmailTemplatesListPage = lazy(() =>
  import('../pages/mineminer/EmailTemplatesListPage').then((m) => ({
    default: m.EmailTemplatesListPage,
  })),
);
const MineMinerEmailTemplateDetailPage = lazy(() =>
  import('../pages/mineminer/EmailTemplateDetailPage').then((m) => ({
    default: m.EmailTemplateDetailPage,
  })),
);
const MineMinerReportsPage = lazy(() =>
  import('../pages/mineminer/MineMinerReportsPage').then((m) => ({
    default: m.MineMinerReportsPage,
  })),
);
// sprint-147 — on-site MSHA 24-month violations analytics page (rep-level).
const MineMinerAnalyticsPage = lazy(() =>
  import('../pages/mineminer/MineMinerAnalyticsPage').then((m) => ({
    default: m.MineMinerAnalyticsPage,
  })),
);
// sprint-152 (epic-020) — Sync offering ↔ CFR mapping review page (rep-level).
const SyncOfferingDefinitionsPage = lazy(() =>
  import('../pages/mineminer/SyncOfferingDefinitionsPage').then((m) => ({
    default: m.SyncOfferingDefinitionsPage,
  })),
);
// epic-025 Sprint D — Account Intelligence executive briefing page (rep-level).
const AccountIntelligencePage = lazy(() =>
  import('../pages/AccountIntelligence/AccountIntelligencePage').then((m) => ({
    default: m.AccountIntelligencePage,
  })),
);
const MineMinerAuditPage = lazy(() =>
  import('../pages/mineminer/MineMinerAuditPage').then((m) => ({
    default: m.MineMinerAuditPage,
  })),
);
// sprint-108 S2 — auth-gated change-password page; entry point is the
// Header avatar dropdown.
const ChangePasswordPage = lazy(() =>
  import('../pages/ChangePassword/ChangePasswordPage').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);

// sprint-217 (epic-036 Phase 1 increment 1D) — the client area's landing surface.
const ClientMinesPage = lazy(() =>
  import('../pages/client/ClientMinesPage').then((m) => ({ default: m.ClientMinesPage })),
);
// sprint-252 (4H, DR-041) — the Legal portfolio landing.
const ClientPortfolioPage = lazy(() =>
  import('../pages/client/ClientPortfolioPage').then((m) => ({
    default: m.ClientPortfolioPage,
  })),
);

// sprint-217 goal 3 — the per-mine surfaces.
const ClientMinePage = lazy(() =>
  import('../pages/client/ClientMinePage').then((m) => ({ default: m.ClientMinePage })),
);
const ClientViolationsPage = lazy(() =>
  import('../pages/client/ClientViolationsPage').then((m) => ({
    default: m.ClientViolationsPage,
  })),
);
// sprint-229 (4A/N3/U2) — one citation as the §5.9 pair.
const ClientViolationDetailPage = lazy(() =>
  import('../pages/client/ClientViolationDetailPage').then((m) => ({
    default: m.ClientViolationDetailPage,
  })),
);
const ClientInspectionsPage = lazy(() =>
  import('../pages/client/ClientInspectionsPage').then((m) => ({
    default: m.ClientInspectionsPage,
  })),
);
// sprint-269 (S005) — the live inspection-day triage surface.
const ClientInspectionTriagePage = lazy(() =>
  import('../pages/client/ClientInspectionTriagePage').then((m) => ({
    default: m.ClientInspectionTriagePage,
  })),
);

// sprint-217 goals 4b+5 — the ADR-005 verify gate.
const ClientTasksPage = lazy(() =>
  import('../pages/client/ClientTasksPage').then((m) => ({
    default: m.ClientTasksPage,
  }))
);
// sprint-229 (4A/F6) — the internal-target ladder.
const ClientReviewQueuePage = lazy(() =>
  import('../pages/client/ClientReviewQueuePage').then((m) => ({
    default: m.ClientReviewQueuePage,
  }))
);

// sprint-231 (4C) — the reconciliation queue.
const ClientEvidenceIntegrityPage = lazy(() =>
  import('../pages/client/ClientEvidenceIntegrityPage').then((m) => ({
    default: m.ClientEvidenceIntegrityPage,
  }))
);

const ClientInspectorsPage = lazy(() =>
  import('../pages/client/ClientInspectorsPage').then((m) => ({
    default: m.ClientInspectorsPage,
  }))
);

const ClientCorrespondencePage = lazy(() =>
  import('../pages/client/ClientCorrespondencePage').then((m) => ({
    default: m.ClientCorrespondencePage,
  }))
);

const ClientCorrespondencePacketPage = lazy(() =>
  import('../pages/client/ClientCorrespondencePacketPage').then((m) => ({
    default: m.ClientCorrespondencePacketPage,
  }))
);

const ClientReconciliationPage = lazy(() =>
  import('../pages/client/ClientReconciliationPage').then((m) => ({
    default: m.ClientReconciliationPage,
  }))
);

const ClientDeadlinePolicyPage = lazy(() =>
  import('../pages/client/ClientDeadlinePolicyPage').then((m) => ({
    default: m.ClientDeadlinePolicyPage,
  }))
);

const ClientTaskDetailPage = lazy(() =>
  import('../pages/client/ClientTaskDetailPage').then((m) => ({
    default: m.ClientTaskDetailPage,
  }))
);

const ClientNotificationsPage = lazy(() =>
  import('../pages/client/ClientNotificationsPage').then((m) => ({
    default: m.ClientNotificationsPage,
  }))
);

const ClientConfirmCitationPage = lazy(() =>
  import('../pages/client/ClientConfirmCitationPage').then((m) => ({
    default: m.ClientConfirmCitationPage,
  })),
);

// Public demo-scheduling landing (epic-011 Sprint D / sprint-098).
const ScheduleDemoPage = lazy(() =>
  import('../pages/ScheduleDemo/ScheduleDemoPage').then((m) => ({
    default: m.ScheduleDemoPage,
  })),
);

// Sync Mine Safety LinkedIn drafts for review — sign-in only, any role.
const SyncLinkedInPage = lazy(() =>
  import('../pages/SyncLinkedIn/SyncLinkedInPage').then((m) => ({ default: m.SyncLinkedInPage })),
);

// MyMineMiner LinkedIn drafts for internal review — MineMiner admins only.
const MyMineMinerLinkedInPage = lazy(() =>
  import('../pages/MyMineMinerLinkedIn/MyMineMinerLinkedInPage').then((m) => ({
    default: m.MyMineMinerLinkedInPage,
  })),
);

// sprint-279 S003 (F23 slice 3) — the outside contact's step page.
const ExternalStepPage = lazy(() =>
  import('../pages/ExternalStep/ExternalStepPage').then((m) => ({
    default: m.ExternalStepPage,
  })),
);

// sprint-130 — Elko 2026 rep field briefing. Root path, but registered inside the
// role-gated tree below so it inherits the rep auth-gate + MineMinerLayout chrome.
const Elko2026Page = lazy(() =>
  import('../pages/Elko2026/Elko2026Page').then((m) => ({ default: m.Elko2026Page })),
);

// Tata Chemicals Mine fatality briefing (/Tata) — same root-path, auth-gated shape.
const TataBriefingPage = lazy(() =>
  import('../pages/TataBriefing/TataBriefingPage').then((m) => ({ default: m.TataBriefingPage })),
);

// sprint-235 — Hecla Mining Company account overview (/Hecla) — same root-path, auth-gated shape.
const HeclaBriefingPage = lazy(() =>
  import('../pages/HeclaBriefing/HeclaBriefingPage').then((m) => ({
    default: m.HeclaBriefingPage,
  })),
);

// sprint-250 (epic-039 increment F) — the five curated account overviews on one shared template.
// Both components are registered TWICE below: once in the staff tree and once in the organization
// tree. The two gates are mutually exclusive by design, so a single registration could serve only
// one of the two audiences the increment is for.
const AccountOverviewsIndexPage = lazy(() =>
  import('../pages/AccountOverviews/AccountOverviewsIndexPage').then((m) => ({
    default: m.AccountOverviewsIndexPage,
  })),
);
const AccountOverviewRoutePage = lazy(() =>
  import('../pages/AccountOverviews/AccountOverviewRoutePage').then((m) => ({
    default: m.AccountOverviewRoutePage,
  })),
);

/**
 * AppRoutes — the MineMiner SPA's route table.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). The apex `/` redirects to
 * `/MineMiner` when authenticated and `/login` otherwise; everything else is
 * either the role-gated `/MineMiner/*` tree, the public demo-scheduling page,
 * the login screen, or the utility surfaces.
 */
export const AppRoutes: React.FC = (): React.ReactElement => {
  return (
    <Routes>
      {/* Apex `/` — auth-aware redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Login */}
      <Route path={PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />

      {/* sprint-107 S1.5 (epic-013 Sprint D, Stream A item A2) — carved
          forgot/reset password flow on the carved host. */}
      <Route path={PUBLIC_ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={PUBLIC_ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path={PUBLIC_ROUTES.ACCEPT_INVITE} element={<AcceptInvitePage />} />

      {/* Public demo-scheduling landing (epic-011 Sprint D) */}
      <Route
        path={PUBLIC_ROUTES.SCHEDULE_DEMO}
        element={
          <Suspense fallback={<PageLoader />}>
            <ScheduleDemoPage />
          </Suspense>
        }
      />

      {/* Sign-in only, any role: Sync LinkedIn drafts shared for review. */}
      <Route element={<ProtectedRoute />}>
        <Route
          path={SIGNED_IN_ROUTES.SYNC_LINKEDIN_DRAFTS}
          element={
            <Suspense fallback={<PageLoader />}>
              <SyncLinkedInPage />
            </Suspense>
          }
        />
      </Route>

      {/* MineMiner admins only: MyMineMiner LinkedIn drafts for internal review. */}
      <Route element={<MineMinerAdminRoute />}>
        <Route
          path={ADMIN_ONLY_ROUTES.MYMINEMINER_LINKEDIN_DRAFTS}
          element={
            <Suspense fallback={<PageLoader />}>
              <MyMineMinerLinkedInPage />
            </Suspense>
          }
        />
      </Route>

      {/* sprint-279 S003 (F23 slice 3) — outside contact's step link, no login */}
      <Route
        path={PUBLIC_ROUTES.EXTERNAL_STEP}
        element={
          <Suspense fallback={<PageLoader />}>
            <ExternalStepPage />
          </Suspense>
        }
      />

      {/* Role-gated MineMiner surface */}
      <Route element={<MineMinerRoute />}>
        <Route element={<MineMinerLayout />}>
          {/* sprint-130 — Elko 2026 briefing at root /Elko2026, auth-gated + chromed. */}
          <Route
            path={MINEMINER_ROUTES.ELKO_2026}
            element={
              <Suspense fallback={<PageLoader />}>
                <Elko2026Page />
              </Suspense>
            }
          />
          {/* Tata Chemicals fatality briefing at root /Tata — same auth-gated shape. */}
          <Route
            path={MINEMINER_ROUTES.TATA_BRIEFING}
            element={
              <Suspense fallback={<PageLoader />}>
                <TataBriefingPage />
              </Suspense>
            }
          />
          {/* Hecla account overview at root /Hecla — same auth-gated shape (sprint-235). */}
          <Route
            path={MINEMINER_ROUTES.HECLA_BRIEFING}
            element={
              <Suspense fallback={<PageLoader />}>
                <HeclaBriefingPage />
              </Suspense>
            }
          />
          {/* sprint-250 — the STAFF door to the five curated account overviews. The organization
              door is the matching pair inside the OrganizationRoute tree further down. */}
          <Route
            path={MINEMINER_ROUTES.ACCOUNT_OVERVIEWS}
            element={
              <Suspense fallback={<PageLoader />}>
                <AccountOverviewsIndexPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.ACCOUNT_OVERVIEW_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <AccountOverviewRoutePage />
              </Suspense>
            }
          />
          {/* sprint-173 (TECH_DEBT #127): the apex `/MineMiner` now lands on Account
              Intelligence (the default post-login surface); the Mines list moved to its own
              path `/MineMiner/mines`. */}
          <Route
            path={MINEMINER_ROUTES.BASE}
            element={<Navigate to={MINEMINER_ROUTES.ACCOUNT_INTELLIGENCE} replace />}
          />
          <Route
            path={MINEMINER_ROUTES.MINES}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerMinesListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.LEADS_NEW_MINE}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerNewMineLeadsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.LEADS_RECENT_CITATION}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerRecentCitationLeadsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.LEADS_IMMINENT}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerImminentDangerLeadsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.LEAD_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerLeadDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.MINE_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerMineDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.MINE_INSPECTION_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerMineInspectionDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.VIOLATIONS}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerViolationsExplorerPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.ADMIN}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerAdminPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.OPERATORS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerOperatorsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.OPERATOR_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerOperatorDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CONTROLLERS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerControllersListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CONTROLLER_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerControllerDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CONTRACTORS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerContractorsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CONTRACTOR_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerContractorDetailPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.CONTACTS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerContactsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CONTACT_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerContactDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.COMPANIES_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerCompaniesComingSoonPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.CAPTURE}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerMobileCapturePage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CAPTURE_EVENT}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerMobileCapturePage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.EVENTS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerEventsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.EVENT_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerEventDetailPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.EVENT_EXHIBITORS}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerEventExhibitorImportPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.LISTS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerListsPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.LIST_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerListDetailPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.WORK_QUEUE}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerWorkQueuePage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.CAMPAIGNS_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerCampaignsListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CAMPAIGN_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerCampaignDetailPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.EMAIL_TEMPLATES_LIST}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerEmailTemplatesListPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.EMAIL_TEMPLATE_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerEmailTemplateDetailPage />
              </Suspense>
            }
          />

          <Route
            path={MINEMINER_ROUTES.REPORTS}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerReportsPage />
              </Suspense>
            }
          />
          {/* sprint-147 — rep-level MSHA violations analytics page. */}
          <Route
            path={MINEMINER_ROUTES.ANALYTICS}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerAnalyticsPage />
              </Suspense>
            }
          />
          {/* sprint-152 — rep-level Sync offering ↔ CFR mapping review page. */}
          <Route
            path={MINEMINER_ROUTES.OFFERINGS}
            element={
              <Suspense fallback={<PageLoader />}>
                <SyncOfferingDefinitionsPage />
              </Suspense>
            }
          />
          {/* epic-025 Sprint D — rep-level Account Intelligence executive briefing. */}
          <Route
            path={MINEMINER_ROUTES.ACCOUNT_INTELLIGENCE}
            element={
              <Suspense fallback={<PageLoader />}>
                <AccountIntelligencePage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.AUDIT}
            element={
              <Suspense fallback={<PageLoader />}>
                <MineMinerAuditPage />
              </Suspense>
            }
          />
          <Route
            path={MINEMINER_ROUTES.CHANGE_PASSWORD}
            element={
              <Suspense fallback={<PageLoader />}>
                <ChangePasswordPage />
              </Suspense>
            }
          />
        </Route>
      </Route>

      {/* Client area (sprint-217 — epic-036 Phase 1 increment 1D, DR-008).
          A separate guard AND a separate layout from the staff tree above: MineClientRoute
          admits only the three mine-scoped client roles, and MineClientLayout deliberately
          shares no chrome with MineMinerLayout. Neither tree is reachable from the other. */}
      <Route element={<MineClientRoute />}>
        <Route element={<MineClientLayout />}>
          <Route
            path={CLIENT_ROUTES.CLIENT_BASE}
            element={<Navigate to={CLIENT_ROUTES.CLIENT_MINES} replace />}
          />
          <Route
            path={CLIENT_ROUTES.CLIENT_MINES}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientMinesPage />
              </Suspense>
            }
          />

          {/* sprint-252 (4H, DR-041) — the Legal portfolio. A sibling of the mines list, not
              of the per-mine shell: the route is SELF-SCOPED and carries no mineId. Behind
              the same client-role guard as everything here; whether THIS user may read a
              portfolio is the server's decision (RequireMineClientLegalPortfolio), and the
              page answers a refusal with an honest card rather than a hidden route. */}
          <Route
            path={CLIENT_ROUTES.CLIENT_PORTFOLIO}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientPortfolioPage />
              </Suspense>
            }
          />

          {/* goal 3 — one mine, with its violations and inspections nested inside a shell
              that keeps the mine's identity on screen across both. The mineId is a PATH
              segment because that is what MineMembershipRequirement reads server-side, so
              the SPA's shape and the API's authorisation agree on one string. */}
          <Route
            path={CLIENT_ROUTES.CLIENT_MINE}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientMinePage />
              </Suspense>
            }
          >
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_VIOLATIONS}
              element={<ClientViolationsPage />}
            />
            {/* sprint-229 (4A/N3/U2) — one citation as the §5.9 pair. Inside the shell, not a
                sibling like the confirm gate: the detail is one of the places you can be at this
                mine (the violations tab, one level deeper), and the back link returns to the tab
                it came from. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_VIOLATION_DETAIL}
              element={<ClientViolationDetailPage />}
            />
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_INSPECTIONS}
              element={<ClientInspectionsPage />}
            />
            {/* sprint-269 (S005) — the live inspection-day triage surface: the same subject as
                the inspections tab at a different tempo (the walk landing NOW, urgent first,
                assign one click from an item). Admin-gated in the tab strip; the server gates
                the assign act regardless. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_INSPECTION_TRIAGE}
              element={<ClientInspectionTriagePage />}
            />
            {/* sprint-227 (3F) — abatement work joins the shell as a third tab rather than
                becoming a page of its own, because it IS one of the places you can be at this
                mine: the same mine, a different question about it. The shell keeps the mine's
                identity on screen across all three, which is the point of the shell. */}
            <Route path={CLIENT_ROUTES.CLIENT_MINE_TASKS} element={<ClientTasksPage />} />
            {/* sprint-230 (4B) — the review queue: reports filed and awaiting the Admin's
                judgment. In the shell because the queue is a fact about this mine — and it is
                also 4D's future fail-closed destination, which is why it is a tab rather than
                a filter on the tasks tab. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_REVIEW_QUEUE}
              element={<ClientReviewQueuePage />}
            />
            {/* sprint-231 (4C) — the reconciliation queue: the weekly feed's answers to what
                this product asserted. In the shell beside the review queue, because the two are
                the same question one layer apart — "what claims wait on me" and "what has MSHA
                published against the claims I accepted". */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_RECONCILIATION}
              element={<ClientReconciliationPage />}
            />
            {/* sprint-244 (4E-2 Goal 4) — the evidence-integrity register. In the shell beside
                the other two registers: the review queue asks what claims wait on the Admin,
                reconciliation asks what MSHA published against them, and this asks whether the
                evidence underneath either is still the evidence that was filed. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_EVIDENCE_INTEGRITY}
              element={<ClientEvidenceIntegrityPage />}
            />
            {/* sprint-244 (4E-2 Goal 7) — who the answer gets sent to. Last of the registers in
                the shell because it is the act the other three lead to: once a claim is accepted,
                MSHA's record checked and the evidence behind it shown to still stand, the
                remaining question is who to write to. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_INSPECTORS}
              element={<ClientInspectorsPage />}
            />
            {/* sprint-244/S005 (4E-2 Goal 7 part 2) — the envelope; sprint-249/S001 (4F) — the
                send, MSHA's reply, and the packet's feed watch, rendered as two facts. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_CORRESPONDENCE}
              element={<ClientCorrespondencePage />}
            />
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_CORRESPONDENCE_PACKET}
              element={<ClientCorrespondencePacketPage />}
            />
            {/* sprint-229 (4A/F6) — the internal-target ladder, fourth in the shell for the
                same reason: the policy is a fact about THIS mine, and changing it recomputes
                the targets on the tab beside it. */}
            <Route
              path={CLIENT_ROUTES.CLIENT_MINE_DEADLINE_POLICY}
              element={<ClientDeadlinePolicyPage />}
            />
          </Route>

          {/* goals 4b+5 — the verify gate. A SIBLING of the mine shell above, not a tab inside
              it: checking a citation is a task with a beginning and an end, not one of the
              places you can be at this mine, and the tab strip would invite someone to navigate
              away mid-check. React Router ranks by specificity, so this wins over the shell
              even though the shell's path is a prefix of it. */}
          <Route
            path={CLIENT_ROUTES.CLIENT_MINE_CONFIRM_7000_3}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientConfirmCitationPage />
              </Suspense>
            }
          />

          {/* sprint-227 (3F) — one task, a SIBLING of the shell for the same reason the confirm
              page is one: working a task has a beginning and an end, and the tab strip would
              invite someone to navigate away mid-upload. React Router ranks by specificity, so
              this wins over the shell even though the shell's path is a prefix of it. */}
          <Route
            path={CLIENT_ROUTES.CLIENT_MINE_TASK}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientTaskDetailPage />
              </Suspense>
            }
          />

          {/* sprint-227 (3F goal 4) — the notification feed. A SIBLING of the mine shell, not a
              fourth tab: the tabs are places you can be AT THIS MINE — the same mine, a different
              question about it — and a feed is not a view of the mine's data, it is a view of what
              happened to you. It is reached from the chrome's unread badge rather than from the
              tab strip, which is where the count already is. */}
          <Route
            path={CLIENT_ROUTES.CLIENT_MINE_NOTIFICATIONS}
            element={
              <Suspense fallback={<PageLoader />}>
                <ClientNotificationsPage />
              </Suspense>
            }
          />
        </Route>
      </Route>

      {/* Organization area (sprint-239 — epic-039 Canary increment A, DR-002).
          A THIRD guard, beside the staff and client trees rather than inside either.
          OrganizationRoute admits only the two organization roles: a mine-client role does not
          reach here and an organization role does not reach the client tree, because the two
          tenancies are separate lanes and neither implies the other (DR-002 clause 2).

          🔴 The paths carry :organizationId as a PATH SEGMENT, matching the API, because
          OrganizationMembershipRequirement resolves the tenancy from the route value and
          declines a request that names no organization. */}
      <Route element={<OrganizationRoute />}>
        <Route
          path={ORGANIZATION_ROUTES.ORG_BASE}
          element={
            <Suspense fallback={<PageLoader />}>
              <OrganizationIndexPage />
            </Suspense>
          }
        />
        <Route
          path={ORGANIZATION_ROUTES.ORG_HOME}
          element={
            <Suspense fallback={<PageLoader />}>
              <OrganizationHomePage />
            </Suspense>
          }
        />
        {/* sprint-250 (increment F) — the ORGANIZATION door to the same two components the staff
            tree renders. Gated on the CuratedBriefings license, which is separate from Canary's:
            an organization can hold one without the other. */}
        <Route
          path={ORGANIZATION_ROUTES.ORG_BRIEFINGS}
          element={
            <Suspense fallback={<PageLoader />}>
              <AccountOverviewsIndexPage />
            </Suspense>
          }
        />
        <Route
          path={ORGANIZATION_ROUTES.ORG_BRIEFING_DETAIL}
          element={
            <Suspense fallback={<PageLoader />}>
              <AccountOverviewRoutePage />
            </Suspense>
          }
        />
      </Route>

      {/* Utility routes */}
      <Route path={UTILITY_ROUTES.ACCESS_DENIED} element={<AccessDenied />} />
      <Route path={UTILITY_ROUTES.NOT_FOUND} element={<NotFound />} />

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
